import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Shield, CheckCircle2, XCircle, Trash2, ExternalLink, AlertTriangle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const REASON_LABELS = {
  scam: "Scam / fraud",
  prohibited: "Prohibited item",
  spam: "Spam / off-topic",
  harassment: "Harassment",
  misleading: "Misleading",
  other: "Other",
};

const STATUS_STYLES = {
  open: "bg-secondary text-secondary-foreground",
  resolved: "bg-primary text-primary-foreground",
  dismissed: "bg-muted text-muted-foreground",
};

export default function AdminReports() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("open");
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolveReport, setResolveReport] = useState(null);
  const [resolveNotes, setResolveNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return navigate("/login");
    if (!user.is_admin) {
      toast.error("Admin only");
      return navigate("/");
    }
  }, [user, authLoading, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: s }] = await Promise.all([
        api.get("/admin/reports", { params: tab === "all" ? {} : { status: tab } }),
        api.get("/admin/stats"),
      ]);
      setReports(r);
      setStats(s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user]);

  const resolve = async (status) => {
    if (!resolveReport) return;
    setBusy(true);
    try {
      await api.patch(`/admin/reports/${resolveReport.report_id}`, {
        status,
        resolution_notes: resolveNotes,
      });
      toast.success(`Marked ${status}`);
      setResolveReport(null);
      setResolveNotes("");
      load();
    } catch (e) {
      toast.error("Failed");
    } finally {
      setBusy(false);
    }
  };

  const removeTarget = async (r) => {
    if (!window.confirm(`Remove this ${r.target_type}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      if (r.target_type === "listing") {
        await api.post(`/admin/listings/${r.target_id}/remove`);
      } else if (r.target_type === "thread") {
        await api.post(`/admin/threads/${r.target_id}/remove`);
      } else {
        toast.error(`Removal of ${r.target_type} not supported yet`);
        return;
      }
      toast.success(`${r.target_type} removed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const linkFor = (r) => {
    if (r.target_type === "listing") return `/listings/${r.target_id}`;
    if (r.target_type === "thread") return `/threads/${r.target_id}`;
    if (r.target_type === "reply" && r.target_preview?.thread_id) return `/threads/${r.target_preview.thread_id}`;
    if (r.target_type === "user") return `/profile/${r.target_id}`;
    return null;
  };

  if (authLoading || !user?.is_admin) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in" data-testid="admin-reports-page">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-secondary mb-2">
        <Shield className="w-3.5 h-3.5" /> Moderator console
      </div>
      <h1 className="font-display font-extrabold text-3xl md:text-4xl">Reports & moderation</h1>
      <p className="text-muted-foreground mt-2">Review user reports, remove offending content, and keep the community honest.</p>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="admin-stats-grid">
          <StatCard label="Open reports" value={stats.reports_open} highlight={stats.reports_open > 0} />
          <StatCard label="Total users" value={stats.users} />
          <StatCard label="Available listings" value={stats.listings_available} />
          <StatCard label="Active Pass holders" value={stats.subscribers_active} />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="mt-10">
        <TabsList className="bg-muted">
          <TabsTrigger value="open" data-testid="tab-open">Open ({stats?.reports_open ?? 0})</TabsTrigger>
          <TabsTrigger value="resolved" data-testid="tab-resolved">Resolved</TabsTrigger>
          <TabsTrigger value="dismissed" data-testid="tab-dismissed">Dismissed</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-6">
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : reports.length === 0 ? (
            <Card className="bg-card"><CardContent className="py-14 text-center text-muted-foreground">No reports in this bucket.</CardContent></Card>
          ) : (
            <div className="space-y-3" data-testid="admin-reports-list">
              {reports.map((r) => {
                const link = linkFor(r);
                const removable = r.target_type === "listing" || r.target_type === "thread";
                return (
                  <Card key={r.report_id} className="bg-card border-border" data-testid={`admin-report-${r.report_id}`}>
                    <CardContent className="p-5">
                      <div className="flex flex-wrap items-start gap-3">
                        <Badge className={STATUS_STYLES[r.status] || ""}>{r.status}</Badge>
                        <Badge variant="outline" className="border-destructive/30 text-destructive bg-destructive/5">
                          {REASON_LABELS[r.reason] || r.reason}
                        </Badge>
                        <Badge variant="outline">{r.target_type}</Badge>
                        <span className="text-xs text-muted-foreground ml-auto">
                          {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                        </span>
                      </div>

                      {r.target_preview ? (
                        <div className="mt-3">
                          <div className="font-display font-bold text-lg">{r.target_preview.title}</div>
                          <div className="text-sm text-muted-foreground">{r.target_preview.detail}</div>
                          {r.target_preview.status === "removed" && (
                            <Badge className="mt-2 bg-foreground text-background text-[10px]">TARGET REMOVED</Badge>
                          )}
                        </div>
                      ) : (
                        <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                          <AlertTriangle className="w-4 h-4 text-secondary" /> Target no longer exists (may have been deleted)
                        </div>
                      )}

                      {r.details && (
                        <div className="mt-3 text-sm bg-muted/50 rounded-md p-3 whitespace-pre-wrap">
                          <span className="text-xs uppercase tracking-widest text-muted-foreground">Reporter note:</span>{"\n"}
                          {r.details}
                        </div>
                      )}

                      <div className="mt-3 text-xs text-muted-foreground">
                        Reported by <span className="font-medium text-foreground">{r.reporter_name}</span> ({r.reporter_email})
                      </div>

                      {r.status !== "open" && r.resolution_notes && (
                        <div className="mt-3 text-xs bg-primary/5 border-l-2 border-primary rounded-r p-3">
                          <span className="text-primary font-semibold">Resolution:</span> {r.resolution_notes}
                        </div>
                      )}

                      <div className="mt-4 flex flex-wrap gap-2">
                        {link && (
                          <Button size="sm" variant="outline" onClick={() => window.open(link, "_blank")} data-testid={`btn-open-target-${r.report_id}`}>
                            <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open target
                          </Button>
                        )}
                        {r.status === "open" && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => { setResolveReport({ ...r, action: "resolve" }); setResolveNotes(""); }}
                              className="bg-primary hover:bg-primary/90"
                              data-testid={`btn-resolve-${r.report_id}`}
                            >
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Resolve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setResolveReport({ ...r, action: "dismiss" }); setResolveNotes(""); }}
                              data-testid={`btn-dismiss-${r.report_id}`}
                            >
                              <XCircle className="w-3.5 h-3.5 mr-1.5" /> Dismiss
                            </Button>
                            {removable && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => removeTarget(r)}
                                disabled={busy}
                                className="border-destructive/40 text-destructive hover:bg-destructive/5"
                                data-testid={`btn-remove-${r.report_id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove {r.target_type}
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!resolveReport} onOpenChange={(o) => !o && setResolveReport(null)}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle>
              {resolveReport?.action === "resolve" ? "Resolve report" : "Dismiss report"}
            </DialogTitle>
            <DialogDescription>
              Optional notes for the record. Only admins can see these.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={resolveNotes}
            onChange={(e) => setResolveNotes(e.target.value)}
            placeholder={resolveReport?.action === "resolve" ? "e.g. Removed listing, warned seller." : "e.g. No violation found."}
            className="min-h-[100px]"
            data-testid="input-resolution-notes"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveReport(null)} data-testid="btn-cancel-resolve">Cancel</Button>
            <Button
              onClick={() => resolve(resolveReport?.action === "resolve" ? "resolved" : "dismissed")}
              disabled={busy}
              className={resolveReport?.action === "resolve" ? "bg-primary hover:bg-primary/90" : ""}
              data-testid="btn-confirm-resolve"
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirm {resolveReport?.action}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const StatCard = ({ label, value, highlight }) => (
  <Card className={`${highlight ? "border-secondary" : ""}`}>
    <CardContent className="p-4">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-display font-extrabold text-3xl mt-2 ${highlight ? "text-secondary" : "text-foreground"}`}>{value}</div>
    </CardContent>
  </Card>
);
